import { useState } from "react";
import { ChannelEnabledToggle } from "../../atoms/ChannelEnabledToggle";
import { SecretInput } from "../../atoms/SecretInput";
import { TestSendPanel } from "../../molecules/TestSendPanel";
import type { ChannelSettingResponse, TeamsConfig } from "../../../types/subscriptionSettings";
import type { UseChannelSettings } from "../../../hooks/useChannelSettings";

interface Props {
  ctrl: UseChannelSettings;
  setting: ChannelSettingResponse;
}

export function TeamsChannelTab({ ctrl, setting }: Props) {
  const initial = setting.config as TeamsConfig;
  const [enabled, setEnabled] = useState(setting.is_enabled);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [defaultChannel, setDefaultChannel] = useState(initial.default_channel ?? "");
  const [appId, setAppId] = useState(initial.app_id ?? "");
  const [tenantId, setTenantId] = useState(initial.tenant_id ?? "");
  const [botUserEmail, setBotUserEmail] = useState(initial.bot_user_email ?? "");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const errors = ctrl.fieldErrors.teams;

  async function handleSave() {
    setSaveStatus("saving");
    const config: Record<string, unknown> = {};
    if (defaultChannel) config.default_channel = defaultChannel;
    if (appId) config.app_id = appId;
    if (tenantId) config.tenant_id = tenantId;
    if (botUserEmail) config.bot_user_email = botUserEmail;
    if (webhookUrl) config.webhook_url = webhookUrl;
    if (clientSecret) config.client_secret = clientSecret;
    const result = await ctrl.save("teams", { is_enabled: enabled, config });
    setSaveStatus(result ? "saved" : "error");
    if (result) {
      setWebhookUrl("");
      setClientSecret("");
    }
  }

  return (
    <div className="ac-channel-tab">
      <div className="ac-channel-tab__header">
        <h2 className="ac-channel-tab__title">Microsoft Teams</h2>
        <ChannelEnabledToggle enabled={enabled} onChange={setEnabled} />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="teams-webhook">Webhook URL (for channel posts)</label>
        <SecretInput id="teams-webhook" value={webhookUrl} onChange={setWebhookUrl} isSet={initial.webhook_url?.is_set ?? false} ariaLabel="Teams webhook URL" />
        {errors.webhook_url && <span className="ac-form-row__error">{errors.webhook_url.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="teams-channel">Default channel (optional)</label>
        <input id="teams-channel" className="ac-input" value={defaultChannel} onChange={(e) => setDefaultChannel(e.target.value)} placeholder="#general" />
      </div>

      <hr style={{ border: "none", borderTop: "1px solid var(--aptean-border-default, #e2e4e9)", margin: "16px 0" }} />
      <p style={{ fontSize: "0.8rem", color: "var(--aptean-color-text-secondary)", margin: "0 0 12px" }}>
        For direct user messages (1:1 chat), configure an Azure AD app with <code>Chat.Create</code> and <code>Teamwork.Migrate.All</code> Application permissions (with admin consent). Provide a service account email as the Bot User — this user will appear as the sender in the chat.
      </p>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="teams-app">App ID</label>
        <input id="teams-app" className="ac-input" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="Azure AD Application (client) ID" />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="teams-tenant">Tenant ID</label>
        <input id="teams-tenant" className="ac-input" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="Azure AD Directory (tenant) ID" />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="teams-bot-user">Bot User Email (service account)</label>
        <input id="teams-bot-user" className="ac-input" value={botUserEmail} onChange={(e) => setBotUserEmail(e.target.value)} placeholder="notifications@yourcompany.com" />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="teams-secret">Client Secret</label>
        <SecretInput id="teams-secret" value={clientSecret} onChange={setClientSecret} isSet={initial.client_secret?.is_set ?? false} ariaLabel="Teams app client secret" />
        {errors.client_secret && <span className="ac-form-row__error">{errors.client_secret.join(" ")}</span>}
      </div>

      <div className="ac-button-row">
        <button type="button" className="ac-button" onClick={handleSave} disabled={saveStatus === "saving"}>
          {saveStatus === "saving" ? "Saving..." : "Save"}
        </button>
        {saveStatus === "saved" && <span className="ac-alert ac-alert--success" role="status">Saved.</span>}
        {saveStatus === "error" && <span className="ac-alert ac-alert--error" role="status">Failed to save.</span>}
      </div>

      <TestSendPanel
        destinationLabel="Send to (user email or webhook URL)"
        placeholder="user@company.com or https://..."
        disabled={!enabled}
        onSend={(dest) => ctrl.test("teams", dest)}
      />
    </div>
  );
}
